import { useState } from "react";

// Module-level set — keeps hints dismissed within the same session even if the
// Redux value hasn't propagated yet (e.g. during the async API round-trip).
const dismissed = new Set();

/**
 * Dismissible hint, backed by Redux state when provided.
 *
 * @param {string} key - unique hint key
 * @param {{
 *   enabled?: boolean,
 *   value?: boolean,
 *   onDismiss?: () => void,
 * }} opts
 *   enabled:   when false this instance never shows the hint
 *   value:     external dismissed state (e.g. from Redux); when true, hint is hidden
 *   onDismiss: called on dismiss (e.g. to dispatch a Redux action)
 * @returns {{ visible: boolean, dismiss: () => void }}
 */
export function useHint(key, { enabled = true, value, onDismiss } = {}) {
  const [isDismissedLocally, setDismissedLocally] = useState(() =>
    dismissed.has(key),
  );

  const isDismissed = value !== undefined ? value : isDismissedLocally;

  const dismiss = () => {
    dismissed.add(key);
    setDismissedLocally(true);
    onDismiss?.();
  };

  return { visible: enabled && !isDismissed, dismiss };
}
